import React, { useEffect, useRef, useState } from 'react';
import { createRoot, Root } from 'react-dom/client';
import type { AgenticChatAdapter, AgenticContext } from 'agentic_chat/types';
import type { Theme } from '@/context/AppContext/AppContextTypes';
import { AIEditsAdapter } from '@/adapters/aiEdits/AIEditsAdapter';

interface AgenticChatShadowWrapperProps {
  adapter: AgenticChatAdapter;
  context: AgenticContext;
  theme?: Theme;
  className?: string;
  iconOverrides?: Record<
    string,
    React.ComponentType<{ width?: number | string; height?: number | string; className?: string }>
  >;
}

/**
 * Shadow DOM wrapper for agentic-chat microfrontend.
 *
 * This component:
 * - Creates a Shadow DOM for complete style isolation
 * - Uses prefetched CSS from AIEditsAdapter (falls back to fetching if not cached)
 * - Renders the React microfrontend inside the isolated shadow tree
 *
 * This prevents content-studio styles from affecting agentic-chat and vice versa.
 *
 * Performance: CSS is prefetched via adapter.initialize() for instant rendering.
 *
 * Error Handling:
 * - Async errors from useEffect are captured in state
 * - Errors are thrown synchronously during render to trigger parent ErrorBoundary
 * - Parent provides error UI with retry functionality
 */
export function AgenticChatShadowWrapper({
  adapter,
  context,
  theme,
  className,
  iconOverrides
}: AgenticChatShadowWrapperProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const shadowRootRef = useRef<ShadowRoot | null>(null);
  const reactRootRef = useRef<Root | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;

    const setupShadowDOM = async () => {
      if (!hostRef.current) {
        window?.logger?.warn('[AgenticChatShadowWrapper] hostRef.current is null');
        return;
      }

      try {
        if (!shadowRootRef.current) {
          shadowRootRef.current = hostRef.current.shadowRoot || hostRef.current.attachShadow({ mode: 'open' });

          if (!shadowRootRef.current.querySelector('.agentic-chat-container')) {
            const reactContainer = document.createElement('div');
            reactContainer.className = 'agentic-chat-container';
            reactContainer.style.cssText = 'width: 100%; height: 100%;';
            shadowRootRef.current.appendChild(reactContainer);

            // Use prefetched CSS if available, otherwise fetch on demand
            let cssContent = AIEditsAdapter.getCachedCssContent();
            if (!cssContent) {
              window?.logger?.info('[AgenticChatShadowWrapper] CSS not cached, fetching on demand');
              const stylesModule = await import('agentic_chat/styles');
              cssContent = await stylesModule.fetchCssContent();
            }

            const styleElement = document.createElement('style');
            styleElement.textContent = cssContent;
            shadowRootRef.current.appendChild(styleElement);
          }
        }

        if (!mounted) {
          return;
        }

        const AgenticChatModule = await import('agentic_chat/AgenticChat');
        const AgenticChat = AgenticChatModule.default;

        if (!mounted) {
          return;
        }

        const reactContainer = shadowRootRef.current.querySelector('.agentic-chat-container');
        if (!reactContainer) {
          throw new Error('React container not found in Shadow DOM');
        }

        if (!reactRootRef.current) {
          reactRootRef.current = createRoot(reactContainer);
        }

        reactRootRef.current.render(
          <AgenticChat adapter={adapter} context={context} theme={theme} iconOverrides={iconOverrides} />
        );
      } catch (err) {
        if (mounted) {
          const error = err instanceof Error ? err : new Error(String(err));
          window?.logger?.error('[AgenticChatShadowWrapper] Failed to setup Shadow DOM:', error);
          setError(error);
        }
      }
    };

    setupShadowDOM();

    return () => {
      mounted = false;
      // Note: React root intentionally not unmounted to preserve state across sidebar open/close
    };
  }, [adapter, context, theme, className, iconOverrides]);

  // Throw error synchronously during render so ErrorBoundary can catch it
  if (error) {
    throw error;
  }

  return <div ref={hostRef} className="size-full" />;
}

export default AgenticChatShadowWrapper;
